import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  query,
  runTransaction,
  setDoc,
  serverTimestamp,
  updateDoc,
  where
} from 'firebase/firestore'
import { db } from '../firebase'

const LIVE_SESSIONS_COLLECTION = 'liveSessions'
const LIVE_RUNTIME_COLLECTION = 'runtime'
const LIVE_RUNTIME_STATE_DOC = 'state'
const LIVE_STATUSES_ACTIVE = ['collecting', 'correction_released']
const LIVE_SESSION_AUTO_CLOSE_MS = 2 * 60 * 60 * 1000
const LIVE_INPUT_MODES = new Set(['functions', 'qcm', 'full'])
const LIVE_REMOTE_COMMAND_TYPES = new Set([
  'play',
  'pause',
  'seek_relative',
  'seek_absolute',
  'seek_start',
  'marker_prev',
  'marker_next',
  'show_correction',
  'next_exercise',
  'prev_exercise',
  'timer_start',
  'timer_pause',
  'timer_reset',
  'timer_hide',
  'timer_set_duration'
])

function normalizeQueue(items = []) {
  const list = []
  items.forEach((item, index) => {
    const exerciseId = String(item?.exerciseId || '').trim()
    if (!exerciseId) return
    list.push({
      exerciseId,
      exerciseTitle: String(item?.exerciseTitle || '').trim() || 'Exercice',
      order: Number.isFinite(item?.order) ? item.order : index
    })
  })
  return list
    .sort((a, b) => a.order - b.order)
    .map((item, index) => ({ ...item, order: index }))
}

function fromDoc(snap) {
  return { id: snap.id, ...snap.data() }
}

function normalizeInputMode(mode) {
  const safeMode = String(mode || '').trim()
  return LIVE_INPUT_MODES.has(safeMode) ? safeMode : 'full'
}

function getLiveRuntimeStateRef(sessionId) {
  return doc(db, LIVE_SESSIONS_COLLECTION, sessionId, LIVE_RUNTIME_COLLECTION, LIVE_RUNTIME_STATE_DOC)
}

export async function getTeacherLiveSessions(teacherId, maxCount = 20) {
  if (!teacherId) return []
  const q = query(
    collection(db, LIVE_SESSIONS_COLLECTION),
    where('teacherId', '==', teacherId),
    limit(Math.max(20, Number(maxCount) > 0 ? Number(maxCount) * 3 : 60))
  )
  const snapshot = await getDocs(q)
  const sessions = snapshot.docs.map(fromDoc)
  const now = Date.now()
  const closedIds = new Set()
  await Promise.all(
    sessions
      .filter((session) => LIVE_STATUSES_ACTIVE.includes(session.status))
      .filter((session) => {
        const ts = session.updatedAt?.toDate?.()?.getTime?.()
          ?? session.createdAt?.toDate?.()?.getTime?.()
          ?? 0
        return ts > 0 && (now - ts) > LIVE_SESSION_AUTO_CLOSE_MS
      })
      .map((session) => {
        closedIds.add(session.id)
        return updateDoc(doc(db, LIVE_SESSIONS_COLLECTION, session.id), {
          status: 'closed',
          closedAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        })
      })
  )
  return sessions
    .map((session) => (
    closedIds.has(session.id) ? { ...session, status: 'closed' } : session
    ))
    .sort((a, b) => {
      const da = a.createdAt?.toDate?.()?.getTime?.() ?? 0
      const db = b.createdAt?.toDate?.()?.getTime?.() ?? 0
      return db - da
    })
    .slice(0, Number(maxCount) > 0 ? Number(maxCount) : 20)
}

export async function getLiveSessionById(sessionId) {
  if (!sessionId) return null
  const ref = doc(db, LIVE_SESSIONS_COLLECTION, sessionId)
  const snap = await getDoc(ref)
  if (!snap.exists()) return null
  return fromDoc(snap)
}

export async function getActiveLiveSessionForClass(teacherClassId, options = {}) {
  if (!teacherClassId) return null
  const teacherIdFilter = options?.teacherId ? String(options.teacherId) : ''
  const q = teacherIdFilter
    ? query(
      collection(db, LIVE_SESSIONS_COLLECTION),
      where('teacherClassId', '==', teacherClassId),
      where('teacherId', '==', teacherIdFilter),
      limit(25)
    )
    : query(
      collection(db, LIVE_SESSIONS_COLLECTION),
      where('teacherClassId', '==', teacherClassId),
      limit(25)
    )
  const snapshot = await getDocs(q)
  if (snapshot.empty) return null
  const list = snapshot.docs
    .map(fromDoc)
    .filter((s) => LIVE_STATUSES_ACTIVE.includes(s.status))
    .sort((a, b) => {
      const da = a.createdAt?.toDate?.()?.getTime?.() ?? 0
      const db = b.createdAt?.toDate?.()?.getTime?.() ?? 0
      return db - da
    })
  return list[0] || null
}

export function subscribeActiveLiveSessionForClass(teacherClassId, onData, onError) {
  if (!teacherClassId) {
    onData?.(null)
    return () => {}
  }
  const q = query(
    collection(db, LIVE_SESSIONS_COLLECTION),
    where('teacherClassId', '==', teacherClassId),
    limit(25)
  )
  return onSnapshot(
    q,
    (snapshot) => {
      if (snapshot.empty) {
        onData?.(null)
        return
      }
      const list = snapshot.docs
        .map(fromDoc)
        .filter((s) => LIVE_STATUSES_ACTIVE.includes(s.status))
        .sort((a, b) => {
          const da = a.createdAt?.toDate?.()?.getTime?.() ?? 0
          const db = b.createdAt?.toDate?.()?.getTime?.() ?? 0
          return db - da
        })
      onData?.(list[0] || null)
    },
    onError
  )
}

export function subscribeLiveSessionById(sessionId, onData, onError) {
  if (!sessionId) {
    onData?.(null)
    return () => {}
  }
  const ref = doc(db, LIVE_SESSIONS_COLLECTION, sessionId)
  return onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) onData?.(null)
      else onData?.(fromDoc(snap))
    },
    onError
  )
}

export async function createLiveSession({ teacherId, teacherClassId, exerciseQueue = [], timerDurationSec = 120, inputMode = 'full' }) {
  if (!teacherId) throw new Error('teacherId requis.')
  if (!teacherClassId) throw new Error('teacherClassId requis.')
  const queue = normalizeQueue(exerciseQueue)
  if (queue.length === 0) throw new Error('Ajoutez au moins un exercice à la playlist.')

  const active = await getActiveLiveSessionForClass(teacherClassId, { teacherId })
  if (active) throw new Error('Une session live est déjà active pour cette classe.')

  const ref = await addDoc(collection(db, LIVE_SESSIONS_COLLECTION), {
    teacherId,
    teacherClassId,
    status: 'collecting',
    inputMode: normalizeInputMode(inputMode),
    exerciseQueue: queue,
    currentExerciseIndex: 0,
    timer: {
      durationSec: Number(timerDurationSec) > 0 ? Number(timerDurationSec) : 120,
      startedAt: null,
      enabled: false,
      paused: false,
      remainingSec: Number(timerDurationSec) > 0 ? Number(timerDurationSec) : 120
    },
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    closedAt: null
  })
  return { id: ref.id }
}

async function ensureOwnership(sessionId, teacherId) {
  const ref = doc(db, LIVE_SESSIONS_COLLECTION, sessionId)
  const snap = await getDoc(ref)
  if (!snap.exists()) throw new Error('Session introuvable.')
  const data = snap.data()
  if (data.teacherId !== teacherId) throw new Error('Session non autorisée.')
  return { ref, data }
}

export async function setLiveSessionStatus(sessionId, teacherId, status, options = {}) {
  if (!['collecting', 'correction_released', 'closed'].includes(status)) {
    throw new Error('Statut de session invalide.')
  }
  const { ref } = await ensureOwnership(sessionId, teacherId)
  const summary = options && typeof options === 'object' ? options.summary : null
  const updates = {
    status,
    updatedAt: serverTimestamp()
  }
  if (status === 'closed') {
    updates.closedAt = serverTimestamp()
    if (summary && typeof summary === 'object') {
      updates.summary = {
        participants: Number(summary.participants || 0),
        classSize: Number(summary.classSize || 0),
        exerciseCount: Number(summary.exerciseCount || 0),
        averageScore: Number(summary.averageScore || 0),
        aboveThresholdPct: Number(summary.aboveThresholdPct || 0),
        topStudentName: String(summary.topStudentName || ''),
        topStudentScore: Number(summary.topStudentScore || 0),
        durationMinutes: Number(summary.durationMinutes || 0),
        closedAtMs: Number(summary.closedAtMs || 0)
      }
    }
  }
  await updateDoc(ref, updates)
}

export async function advanceLiveSessionExercise(sessionId, teacherId) {
  const { ref, data } = await ensureOwnership(sessionId, teacherId)
  const queue = Array.isArray(data.exerciseQueue) ? data.exerciseQueue : []
  const current = Number(data.currentExerciseIndex || 0)
  if (current >= queue.length - 1) {
    throw new Error('Dernier exercice atteint.')
  }
  await updateDoc(ref, {
    currentExerciseIndex: current + 1,
    status: 'collecting',
    updatedAt: serverTimestamp()
  })
}

export async function rewindLiveSessionExercise(sessionId, teacherId) {
  const { ref, data } = await ensureOwnership(sessionId, teacherId)
  const queue = Array.isArray(data.exerciseQueue) ? data.exerciseQueue : []
  const current = Number(data.currentExerciseIndex || 0)
  if (queue.length === 0) {
    throw new Error('Aucun exercice dans la playlist.')
  }
  if (current <= 0) {
    throw new Error('Premier exercice déjà actif.')
  }
  await updateDoc(ref, {
    currentExerciseIndex: current - 1,
    status: 'collecting',
    updatedAt: serverTimestamp()
  })
}

export async function setLiveSessionTimer(sessionId, teacherId, timerPatch = {}) {
  const { ref, data } = await ensureOwnership(sessionId, teacherId)
  const prev = data.timer || {}
  await updateDoc(ref, {
    timer: {
      durationSec: Number.isFinite(timerPatch.durationSec) ? Number(timerPatch.durationSec) : (prev.durationSec || 120),
      startedAt: timerPatch.startedAt ?? prev.startedAt ?? null,
      enabled: typeof timerPatch.enabled === 'boolean' ? timerPatch.enabled : !!prev.enabled,
      paused: typeof timerPatch.paused === 'boolean' ? timerPatch.paused : !!prev.paused,
      remainingSec: Number.isFinite(timerPatch.remainingSec) ? Number(timerPatch.remainingSec) : (prev.remainingSec ?? prev.durationSec ?? 120)
    },
    updatedAt: serverTimestamp()
  })
}

export async function saveLiveSessionAggregate(sessionId, teacherId, exerciseId, payload = {}) {
  if (!sessionId || !exerciseId) return
  const { data } = await ensureOwnership(sessionId, teacherId)
  const currentItem = (Array.isArray(data.exerciseQueue) ? data.exerciseQueue : []).find((item) => item.exerciseId === exerciseId)
  if (!currentItem) throw new Error('Exercice non trouvé dans la playlist.')

  const ref = doc(db, LIVE_SESSIONS_COLLECTION, sessionId, 'aggregates', exerciseId)
  await setDoc(ref, {
    ...payload,
    updatedAt: serverTimestamp()
  }, { merge: true })
}

export async function getLiveSessionAggregate(sessionId, exerciseId) {
  if (!sessionId || !exerciseId) return null
  const ref = doc(db, LIVE_SESSIONS_COLLECTION, sessionId, 'aggregates', exerciseId)
  const snap = await getDoc(ref)
  if (!snap.exists()) return null
  return { id: snap.id, ...snap.data() }
}

export async function saveLiveSessionCurrentRanking(sessionId, teacherId, payload = {}) {
  if (!sessionId) return
  await ensureOwnership(sessionId, teacherId)
  const ref = doc(db, LIVE_SESSIONS_COLLECTION, sessionId, 'aggregates', 'current_ranking')
  await setDoc(ref, {
    ...payload,
    updatedAt: serverTimestamp()
  }, { merge: true })
}

export async function getLiveSessionCurrentRanking(sessionId) {
  if (!sessionId) return null
  const ref = doc(db, LIVE_SESSIONS_COLLECTION, sessionId, 'aggregates', 'current_ranking')
  const snap = await getDoc(ref)
  if (!snap.exists()) return null
  return { id: snap.id, ...snap.data() }
}

export async function saveLiveSessionFinalRanking(sessionId, teacherId, payload = {}) {
  if (!sessionId) return
  await ensureOwnership(sessionId, teacherId)
  const ref = doc(db, LIVE_SESSIONS_COLLECTION, sessionId, 'aggregates', 'final_ranking')
  await setDoc(ref, {
    ...payload,
    isFinal: true,
    updatedAt: serverTimestamp()
  }, { merge: true })
}

export async function getLiveSessionFinalRanking(sessionId) {
  if (!sessionId) return null
  const ref = doc(db, LIVE_SESSIONS_COLLECTION, sessionId, 'aggregates', 'final_ranking')
  const snap = await getDoc(ref)
  if (!snap.exists()) return null
  return { id: snap.id, ...snap.data() }
}

function getCombinedRankingAggregateId(sourceSessionId) {
  const safe = String(sourceSessionId || '')
    .trim()
    .replace(/[^a-zA-Z0-9_-]/g, '_')
  return safe ? `combined_${safe}` : ''
}

export async function saveLiveSessionCombinedRanking(sessionId, teacherId, sourceSessionId, payload = {}) {
  if (!sessionId || !sourceSessionId) return
  await ensureOwnership(sessionId, teacherId)
  const aggregateId = getCombinedRankingAggregateId(sourceSessionId)
  if (!aggregateId) return
  const ref = doc(db, LIVE_SESSIONS_COLLECTION, sessionId, 'aggregates', aggregateId)
  await setDoc(ref, {
    ...payload,
    updatedAt: serverTimestamp()
  }, { merge: true })
}

export async function getLiveSessionCombinedRanking(sessionId, sourceSessionId) {
  if (!sessionId || !sourceSessionId) return null
  const aggregateId = getCombinedRankingAggregateId(sourceSessionId)
  if (!aggregateId) return null
  const ref = doc(db, LIVE_SESSIONS_COLLECTION, sessionId, 'aggregates', aggregateId)
  const snap = await getDoc(ref)
  if (!snap.exists()) return null
  return { id: snap.id, ...snap.data() }
}

export async function clearLiveSessionCurrentRanking(sessionId, teacherId) {
  if (!sessionId) return
  await ensureOwnership(sessionId, teacherId)
  const ref = doc(db, LIVE_SESSIONS_COLLECTION, sessionId, 'aggregates', 'current_ranking')
  await deleteDoc(ref)
}

export async function clearLiveSessionFinalRanking(sessionId, teacherId) {
  if (!sessionId) return
  await ensureOwnership(sessionId, teacherId)
  const ref = doc(db, LIVE_SESSIONS_COLLECTION, sessionId, 'aggregates', 'final_ranking')
  await deleteDoc(ref)
}

export async function clearLiveSessionAggregate(sessionId, teacherId, exerciseId) {
  if (!sessionId || !exerciseId) return
  const { data } = await ensureOwnership(sessionId, teacherId)
  const currentItem = (Array.isArray(data.exerciseQueue) ? data.exerciseQueue : []).find((item) => item.exerciseId === exerciseId)
  if (!currentItem) throw new Error('Exercice non trouvé dans la playlist.')
  const ref = doc(db, LIVE_SESSIONS_COLLECTION, sessionId, 'aggregates', exerciseId)
  await deleteDoc(ref)
}

function normalizeRemoteCommand(command = {}) {
  const type = String(command?.type || '').trim()
  if (!LIVE_REMOTE_COMMAND_TYPES.has(type)) {
    throw new Error('Commande télécommande invalide.')
  }
  const payload = command && typeof command.payload === 'object' && command.payload !== null
    ? command.payload
    : {}
  return { type, payload }
}

export async function getLiveRuntimeStateBySessionId(sessionId) {
  if (!sessionId) return null
  const ref = getLiveRuntimeStateRef(sessionId)
  const snap = await getDoc(ref)
  if (!snap.exists()) return null
  return { id: snap.id, ...snap.data() }
}

export function subscribeLiveRuntimeStateBySessionId(sessionId, onData, onError) {
  if (!sessionId) {
    onData?.(null)
    return () => {}
  }
  const ref = getLiveRuntimeStateRef(sessionId)
  return onSnapshot(
    ref,
    (snap) => {
      if (!snap.exists()) onData?.(null)
      else onData?.({ id: snap.id, ...snap.data() })
    },
    onError
  )
}

export async function sendLiveRuntimeCommand(sessionId, teacherId, command = {}) {
  if (!sessionId) throw new Error('sessionId requis.')
  if (!teacherId) throw new Error('teacherId requis.')
  const { type, payload } = normalizeRemoteCommand(command)
  const sessionRef = doc(db, LIVE_SESSIONS_COLLECTION, sessionId)
  const runtimeRef = getLiveRuntimeStateRef(sessionId)

  let nextSeq = 0
  try {
    nextSeq = await runTransaction(db, async (tx) => {
      const [sessionSnap, runtimeSnap] = await Promise.all([tx.get(sessionRef), tx.get(runtimeRef)])
      if (!sessionSnap.exists()) throw new Error('Session introuvable.')
      const sessionData = sessionSnap.data()
      if (sessionData.teacherId !== teacherId) throw new Error('Session non autorisée.')
      const runtimeData = runtimeSnap.exists() ? runtimeSnap.data() : {}
      const prevSeq = Number(runtimeData?.command?.seq || sessionData?.remoteCommand?.seq || 0)
      const seq = prevSeq + 1
      tx.set(runtimeRef, {
        command: {
          seq,
          type,
          payload,
          issuedAt: serverTimestamp(),
          issuedBy: teacherId
        },
        updatedAt: serverTimestamp()
      }, { merge: true })
      return seq
    })
  } catch (err) {
    throw err
  }

  return { seq: nextSeq }
}

export async function publishLiveProjectionRuntimeHeartbeat(sessionId, teacherId, extraState = {}) {
  if (!sessionId || !teacherId) return
  const runtimeRef = getLiveRuntimeStateRef(sessionId)
  const projectionPatch = {
    'projection.heartbeatAt': serverTimestamp(),
    'projection.online': true,
    'projection.lastProjectionBy': teacherId
  }
  if (extraState && typeof extraState === 'object') {
    Object.entries(extraState).forEach(([key, value]) => {
      projectionPatch[`projection.${key}`] = value
    })
  }
  await setDoc(runtimeRef, {
    ...projectionPatch,
    updatedAt: serverTimestamp()
  }, { merge: true })
}

export async function setLiveProjectionPlaybackState(sessionId, teacherId, isPlaying) {
  if (!sessionId || !teacherId) return
  const runtimeRef = getLiveRuntimeStateRef(sessionId)
  await setDoc(runtimeRef, {
    'projection.isPlaying': !!isPlaying,
    'projection.lastPlaybackChangeAt': serverTimestamp(),
    'projection.lastProjectionBy': teacherId,
    updatedAt: serverTimestamp()
  }, { merge: true })
}

export async function ackLiveRuntimeCommand(sessionId, teacherId, command = {}, options = {}) {
  if (!sessionId || !teacherId) return
  const seq = Number(command?.seq || 0)
  if (!Number.isFinite(seq) || seq <= 0) return
  const type = String(command?.type || '').trim()
  const status = String(options?.status || 'handled').trim() || 'handled'
  const runtimeRef = getLiveRuntimeStateRef(sessionId)
  await setDoc(runtimeRef, {
    'telemetry.lastHandledSeq': seq,
    'telemetry.lastHandledType': type,
    'telemetry.lastHandledStatus': status,
    'telemetry.lastHandledAt': serverTimestamp(),
    'telemetry.lastHandledBy': teacherId,
    'projection.heartbeatAt': serverTimestamp(),
    'projection.online': true,
    updatedAt: serverTimestamp()
  }, { merge: true })
}

// Compat wrappers while callers migrate.
export async function sendLiveRemoteCommand(sessionId, teacherId, command = {}) {
  return sendLiveRuntimeCommand(sessionId, teacherId, command)
}

export async function publishLiveProjectionHeartbeat(sessionId, teacherId, extraState = {}) {
  return publishLiveProjectionRuntimeHeartbeat(sessionId, teacherId, extraState)
}

export async function ackLiveRemoteCommand(sessionId, teacherId, command = {}, options = {}) {
  return ackLiveRuntimeCommand(sessionId, teacherId, command, options)
}

